import { Component, signal, computed, inject, NgZone, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthHttpService } from '../../services/auth-http.service';
import { OnboardingHttpService } from '../../services/onboarding-http.service';
import {
  LoginRequest, LoginResponse, LoginData, OnboardingData,
  PasswordExpirationWarning
} from '../../types/auth.model';
import { FirstLoginRequest } from '../../types/onboarding.model';
import { ActiveSessionModalComponent, SessionInfo } from '../../components/active-session-modal/active-session-modal';
import { PasswordExpiredModalComponent } from '../../components/password/password-expired-modal/password-expired-modal';
import { RecaptchaService } from '../../services/recaptcha.service';
import { LoadingBarComponent } from '../../components/loading-bar/loading-bar';
import { AppFooterComponent } from '../../components/app-footer/app-footer';
import { LoggerService } from '../../services/logger.service';
import { environment } from '../../../environments/environment';

type LoginMode = 'AD' | 'MASTER_CODE';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, FormsModule, ActiveSessionModalComponent, PasswordExpiredModalComponent, LoadingBarComponent, AppFooterComponent],
  templateUrl: './login.html',
  styleUrls: ['./login.scss']
})
export class LoginComponent implements OnInit, OnDestroy {
  loginMode = signal<LoginMode>('MASTER_CODE');
  username = signal('');
  password = signal('');
  showPassword = signal(false);
  errorMessage = signal('');
  isLoading = signal(false);
  showActiveSessionModal = signal(false);
  sessionInfo = signal<SessionInfo>({ device: '', platform: '', ip: '', userId: '' });
  successMessage = signal('');
  showCaptchaV2 = signal(false);
  showPasswordExpirationModal = signal(false);
  passwordDaysRemaining = signal(0);
  loginProgress = signal(0);
  private pendingLoginData: LoginData | null = null;
  private progressInterval: ReturnType<typeof setInterval> | null = null;

  // ── Computed — memoizados, solo recalculan cuando loginMode cambia ──────────

  /** Etiqueta del campo de usuario */
  readonly usernameLabel = computed(() =>
    this.loginMode() === 'AD' ? 'Usuario de Red' : 'ID Código Maestro'
  );

  /** Placeholder del campo de usuario */
  readonly usernamePlaceholder = computed(() =>
    this.loginMode() === 'AD' ? 'Ingresa tu usuario de red' : 'Ingresa tu ID de código maestro'
  );

  @ViewChild('recaptchaV2Container') recaptchaV2Container!: ElementRef<HTMLDivElement>;

  private router = inject(Router);
  private authService = inject(AuthHttpService);
  private onboardingService = inject(OnboardingHttpService);
  private ngZone = inject(NgZone);
  private recaptchaService = inject(RecaptchaService);
  private logger = inject(LoggerService);

  ngOnInit(): void {
    this.recaptchaService.initialize();
  }

  selectMode(mode: LoginMode): void {
    this.loginMode.set(mode);
    this.username.set('');
    this.password.set('');
    this.errorMessage.set('');
  }

  togglePasswordVisibility(): void {
    this.showPassword.update(value => !value);
  }

  onSubmit(): void {
    this.errorMessage.set('');

    if (!this.username() || !this.password()) {
      this.errorMessage.set('Por favor completa todos los campos');
      return;
    }

    this.performLogin();
  }

  private startProgress(): void {
    this.loginProgress.set(0);
    let current = 0;
    this.progressInterval = setInterval(() => {
      current = current < 85 ? current + Math.random() * 15 : current;
      this.loginProgress.set(Math.min(current, 85));
    }, 400);
  }

  private finishProgress(): void {
    if (this.progressInterval) clearInterval(this.progressInterval);
    this.loginProgress.set(100);
    setTimeout(() => this.loginProgress.set(0), 500);
  }

  /**
   * Construye el LoginRequest a partir del estado actual del componente.
   * Centraliza la construcción para evitar duplicación entre performLogin()
   * y retryLoginWithV2Token().
   */
  private buildLoginRequest(captchaToken?: string | null, captchaTokenV2?: string | null): LoginRequest {
    return {
      identifier: this.username(),
      password: this.password(),
      pais: environment.countryCode,
      deviceInfo: {
        deviceId: 'web-fe-angular',
        userAgent: navigator.userAgent,
        platform: 'WEB'
      },
      captchaToken,
      captchaTokenV2,
    };
  }

  private async performLogin(): Promise<void> {
    this.isLoading.set(true);
    this.startProgress();

    const recaptchaToken = await this.recaptchaService.executeAction(environment.recaptchaAction);
    const request = this.buildLoginRequest(recaptchaToken);

    this.authService.login(request).subscribe({
      next: (response: LoginResponse) => {
        this.logger.log('[LoginComponent] response.error?.code:', response.error?.code);
        if (response.error?.code === 'SESSION_ALREADY_ACTIVE') {
          this.sessionInfo.set({
            device: response.sessionData?.deviceInfo.deviceId || '',
            platform: response.sessionData?.deviceInfo.platform || '',
            ip: response.sessionData?.ipAddress || '',
            userId: response.sessionData?.userId || ''
          });
          this.finishProgress();
          this.isLoading.set(false);
          this.showActiveSessionModal.set(true);
          return;
        }
        if (response.error) {
          const errorCode = response.error.code;
          const messageError = response.error.message;

          if (errorCode === 'CAPTCHA_REQUIRED') {
            this.handleCaptchaRequired();
            return;
          }

          this.finishProgress();
          this.isLoading.set(false);
          this.errorMessage.set(messageError || 'Error Desconocido');
          return;
        }

        this.finishProgress();
        this.handleLoginResponse(response, request);
      },
      error: () => {
        this.finishProgress();
        this.isLoading.set(false);
        this.errorMessage.set('Usuario o contraseña incorrectos');
      }
    });
  }

  private handleLoginResponse(response: LoginResponse, requestLogin: LoginRequest): void {
    if (this.loginMode() === 'AD' && response.onboardingData) {
      const { username } = response.onboardingData;
      if (username && username !== 'undefined') {
        localStorage.setItem('fullName', username);
      }
    }

    if (this.loginMode() === 'MASTER_CODE' && response.onboardingData?.commercialStructure) {
      const { commercialStructure } = response.onboardingData;
      const { affiliateName } = commercialStructure;

      if (affiliateName && affiliateName !== 'undefined') {
        localStorage.setItem('fullName', affiliateName);
      }
      localStorage.setItem('commercialStructure', JSON.stringify(commercialStructure));
    }

    localStorage.setItem('loginMode', this.loginMode());

    response.requiresOnboarding
      ? this.handleOnboarding(response.onboardingData!, requestLogin)
      : this.handleSuccessfulLogin(response.data!, response.passwordExpirationWarning);
  }

  private handleOnboarding(data: OnboardingData, requestLogin: LoginRequest): void {
    if (data?.userId) localStorage.setItem('onboardingToken', data.userId);
    if (data?.username) localStorage.setItem('username', data.username);

    this.setEmailMaskedByLoginMode(requestLogin);
    localStorage.setItem('requiresEmailInput', 'true');

    this.callFirstLoginAndNavigate(requestLogin);
  }

  private callFirstLoginAndNavigate(requestLogin: LoginRequest): void {
    const firstLoginRequest: FirstLoginRequest = this.loginMode() === 'AD'
      ? {
        method: this.loginMode(),
        username: requestLogin.identifier,
        password: requestLogin.password,
        pais: requestLogin.pais
      }
      : {
        method: this.loginMode(),
        masterCode: requestLogin.identifier,
        affiliateDocument: requestLogin.password,
        pais: requestLogin.pais
      };

    this.onboardingService.firstLogin(firstLoginRequest).subscribe({
      next: (response) => {
        this.isLoading.set(false);
        localStorage.setItem('onboardingToken', response.userId);
        this.ngZone.run(() => this.router.navigate(['/onboarding/verify-email']));
      },
      error: () => {
        this.isLoading.set(false);
        this.errorMessage.set('Error al iniciar el proceso de onboarding. Por favor intenta de nuevo.');
      }
    });
  }

  private setEmailMaskedByLoginMode(requestLogin: LoginRequest): void {
    if (this.loginMode() === 'AD') {
      localStorage.setItem('emailMasked', requestLogin.identifier || '');
      localStorage.setItem('adEmail', requestLogin.identifier || '');
    }
  }

  private handleSuccessfulLogin(data: LoginData, expirationWarning?: PasswordExpirationWarning): void {
    if (data) this.saveSessionTokens(data);
    if (data?.userProfile?.userId) localStorage.setItem('userId', data.userProfile.userId);

    // FIX: usar el signal directamente en lugar de releer localStorage
    let displayName = data?.userProfile?.displayName;
    const loginMode = this.loginMode();

    if ((!displayName || displayName === 'undefined' || displayName.includes('undefined')) &&
      loginMode === 'MASTER_CODE' && data?.userProfile?.commercialStructure?.affiliateName) {
      displayName = data.userProfile.commercialStructure.affiliateName;
    }

    if (displayName && displayName !== 'undefined' && !displayName.includes('undefined')) {
      localStorage.setItem('fullName', displayName);
    }

    if (data?.userCode) localStorage.setItem('userCode', data.userCode);
    if (data?.userProfile?.email) localStorage.setItem('email', data.userProfile.email);
    if (data?.userProfile?.assignedPOS?.length) {
      localStorage.setItem('assignedPOS', JSON.stringify(data.userProfile.assignedPOS));
    } else {
      localStorage.removeItem('assignedPOS');
    }

    if (expirationWarning && expirationWarning.daysRemaining <= environment.passwordExpirationWarningDays) {
      this.pendingLoginData = data;
      this.passwordDaysRemaining.set(expirationWarning.daysRemaining);
      this.isLoading.set(false);
      this.showPasswordExpirationModal.set(true);
      return;
    }

    this.router.navigate(['/dashboard']);
  }

  private saveSessionTokens(data: LoginData): void {
    if (data?.accessToken) localStorage.setItem('accessToken', data.accessToken);
    if (data?.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
    if (data?.sessionId) localStorage.setItem('sessionId', data.sessionId);
  }

  goToForgotPassword(): void {
    this.router.navigate(['/password/reset-request']);
  }

  goToRegister(): void {
    // TODO: Navigate to register
  }

  /**
   * El modal emite (success) cuando el backend confirmó la invalidación.
   * Cierra el modal y muestra el mensaje de éxito para que el usuario
   * ingrese sus credenciales manualmente (regla de negocio: el re-ingreso
   * siempre debe ser explícito por parte del usuario).
   */
  onSuccessInvalidation(): void {
    this.showActiveSessionModal.set(false);
    this.errorMessage.set('');
    this.successMessage.set('Sesión anterior cerrada exitosamente. Por favor ingresa nuevamente.');
  }

  onCancelSession(): void {
    this.showActiveSessionModal.set(false);
  }

  onChangeExpiredPassword(): void {
    this.showPasswordExpirationModal.set(false);
    this.router.navigate(['/settings/change-password']);
  }

  onSkipPasswordChange(): void {
    this.showPasswordExpirationModal.set(false);
    this.router.navigate(['/dashboard']);
  }

  private handleCaptchaRequired(): void {
    this.finishProgress();
    this.isLoading.set(false);
    this.showCaptchaV2.set(true);
    this.errorMessage.set('Por favor, completa la verificación de seguridad.');

    setTimeout(() => {
      if (this.recaptchaV2Container) {
        this.recaptchaService.renderV2(this.recaptchaV2Container.nativeElement)
          .then(token => this.retryLoginWithV2Token(token))
          .catch(() => {
            this.errorMessage.set('La verificación expiró. Intenta de nuevo.');
            this.recaptchaService.resetV2();
          });
      }
    }, 300);
  }

  private retryLoginWithV2Token(v2Token: string): void {
    this.isLoading.set(true);
    this.errorMessage.set('');

    const request = this.buildLoginRequest(null, v2Token);

    this.authService.login(request).subscribe({
      next: (response: LoginResponse) => {
        this.showCaptchaV2.set(false);
        this.recaptchaService.resetV2();
        if (response.error) {
          this.isLoading.set(false);
          this.errorMessage.set(response.error.message || 'Error Desconocido');
          return;
        }
        this.handleLoginResponse(response, request);
      },
      error: () => {
        this.showCaptchaV2.set(false);
        this.recaptchaService.resetV2();
        this.isLoading.set(false);
        this.errorMessage.set('Usuario o contraseña incorrectos');
      }
    });
  }

  ngOnDestroy(): void {
    this.recaptchaService.resetV2();
  }
}
